// Slovak Payroll Engine - implements the abstract PayrollEngine for Slovak compliance

import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types';
import { PayrollEngine } from '../core/PayrollEngine';
import {
  CountryPayrollConstants,
  PayrollInputData,
  GrossPayComponents,
  InsuranceCalculation,
  TaxCalculation,
  DeductionCalculation,
  ValidationError,
  CalculationError
} from '../core/types';
import { PayrollRateCalculationService } from '../services/PayrollRateCalculationService';

// Import Slovak-specific types
import {
  ContractType,
  PayrollConstants as SlovakPayrollConstants,
  MonthlyPayrollData,
  PayrollCalculation as SlovakPayrollCalculation,
  PayrollDetails,
  HealthInsuranceCompany
} from '@/types/slovak-payroll';

// Import entity-specific calculations
import {
  getEntitySpecificRates,
  calculateEntitySpecificPayroll,
  checkZivnostSocialInsuranceRequirement,
  SlovakEntityType
} from '../slovak/entity-calculations';

/**
 * Slovak Payroll Calculation Engine
 * Implements Slovak-specific payroll calculations including taxes, 
 * social insurance, health insurance, and various deductions
 */
export class SlovakPayrollEngine extends PayrollEngine {
  private employee!: Employee;
  private monthlyData!: MonthlyPayrollData;
  private slovakConstants!: SlovakPayrollConstants;
  private year!: number;
  private businessId!: string;
  private entityType!: SlovakEntityType;

  constructor(countryCode: string = 'SK', debugMode: boolean = false) {
    super(countryCode, debugMode);
  }

  /**
   * Initialize entity-specific settings
   */
  public async initializeEntity(businessId: string): Promise<void> {
    this.businessId = businessId;
    
    // Load business entity type
    const { data: business, error } = await supabase
      .from('businesses')
      .select('slovak_entity_type, business_type')
      .eq('id', businessId)
      .single();
    
    if (error) {
      this.log(`Warning: Could not load business entity type: ${error.message}`);
      this.entityType = 'sro'; // Default to s.r.o.
    } else {
      this.entityType = (business.slovak_entity_type || business.business_type || 'sro') as SlovakEntityType;
    }
    
    this.log(`Initialized Slovak payroll engine for entity type: ${this.entityType}`);
  }

  /**
   * Load Slovak payroll constants from database
   */
  protected async loadPayrollConstants(year: number): Promise<CountryPayrollConstants> {
    this.log(`Loading Slovak payroll constants for year ${year}`);
    
    const { data, error } = await supabase
      .from('payroll_constants')
      .select('*')
      .eq('year', year)
      .single();

    if (error || !data) {
      throw new CalculationError(
        this.countryCode,
        `Failed to load Slovak payroll constants for year ${year}: ${error?.message}`,
        'load_constants',
        { year, error }
      );
    }

    this.slovakConstants = data as SlovakPayrollConstants;
    this.year = year;

    // Convert to generic format
    return {
      countryCode: 'SK',
      year,
      currency: 'EUR',
      minimumWageHourly: this.slovakConstants.minimum_wage_hourly,
      minimumWageMonthly: this.slovakConstants.minimum_wage,
      minimumWageAnnual: this.slovakConstants.minimum_wage * 12,
      standardHoursPerWeek: 40,
      maxHoursPerWeek: 48,
      maxHoursPerDay: 8,
      taxSystem: 'PROGRESSIVE',
      taxBrackets: [
        { min: 0, max: this.slovakConstants.tax_rate_tier1_limit, rate: this.slovakConstants.tax_rate_tier1 },
        { min: this.slovakConstants.tax_rate_tier1_limit, max: null, rate: this.slovakConstants.tax_rate_tier2 }
      ],
      personalAllowance: this.slovakConstants.tax_free_allowance_annual,
      dependentAllowances: {
        child_under_15: this.slovakConstants.child_bonus_under_15,
        child_15_18: this.slovakConstants.child_bonus_15_18,
        spouse: this.slovakConstants.spouse_allowance_annual || 0
      },
      socialInsuranceRates: {
        employee: {
          pension: this.slovakConstants.employee_pension_rate,
          disability: this.slovakConstants.employee_disability_rate,
          sickness: this.slovakConstants.employee_sickness_rate,
          unemployment: this.slovakConstants.employee_unemployment_rate
        },
        employer: {
          pension: this.slovakConstants.employer_pension_rate,
          disability: this.slovakConstants.employer_disability_rate,
          sickness: this.slovakConstants.employer_sickness_rate,
          unemployment: this.slovakConstants.employer_unemployment_rate,
          accident: this.slovakConstants.employer_accident_rate,
          guarantee: this.slovakConstants.employer_guarantee_rate,
          solidarity: this.slovakConstants.employer_solidarity_rate
        }
      },
      healthInsuranceRates: {
        employee: this.slovakConstants.employee_health_rate,
        employer: this.slovakConstants.employer_health_rate
      },
      maxAssessmentBase: this.slovakConstants.max_assessment_base_monthly,
      minAssessmentBase: 0,
      countrySpecificConstants: {
        contractTypes: ['TPP', 'HALF_TIME', 'DoVP', 'DoPČ', 'DoBPŠ'],
        healthInsuranceProviders: ['VšZP', 'Dôvera', 'Union'],
        studentExemptionLimit: this.slovakConstants.student_exemption_limit,
        pensionerExemptionLimit: this.slovakConstants.pensioner_exemption_limit,
        overtimePremiumRate: this.slovakConstants.overtime_premium_rate,
        nightWorkPremium: this.slovakConstants.night_work_premium_hourly,
        holidayPremiumRate: this.slovakConstants.holiday_premium_rate
      },
      validationRules: {
        mandatoryEmployeeFields: ['rodne_cislo', 'contract_type', 'contract_start_date'],
        employeeFieldValidators: {
          rodne_cislo: { type: 'custom', customValidator: 'validateRodneCislo' },
          contract_type: { type: 'string', pattern: '^(TPP|HALF_TIME|DoVP|DoPČ|DoBPŠ)$' }
        },
        contractTypes: ['TPP', 'HALF_TIME', 'DoVP', 'DoPČ', 'DoBPŠ'],
        payFrequencies: ['monthly']
      }
    };
  }

  /**
   * Calculate gross pay components for Slovak employees
   */
  protected async calculateGrossComponents(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<GrossPayComponents> {
    this.log(`Calculating gross components for ${employee.name}`);
    
    this.employee = employee;
    
    // Convert generic input to Slovak monthly data format
    this.monthlyData = this.convertToSlovakMonthlyData(inputData);
    
    // Get standardized hourly rate
    const rateResult = await PayrollRateCalculationService.calculateStandardizedHourlyRate(
      employee,
      employee.roleInfo,
      this.countryCode
    );
    const hourlyRate = rateResult.hourlyRate;
    this.log(`Using standardized hourly rate: €${hourlyRate}/hour (${PayrollRateCalculationService.explainRateCalculation(rateResult)})`);
    
    // Calculate each component
    const basePay = this.monthlyData.regular_hours * hourlyRate;
    const overtimePay = this.monthlyData.overtime_hours * hourlyRate * (1 + this.slovakConstants.overtime_premium_rate);
    
    // Special pay calculations
    const nightWorkPay = (inputData.specialHours.night || 0) * this.slovakConstants.night_work_premium_hourly;
    const weekendPay = this.calculateWeekendPay(inputData.specialHours.weekend || 0);
    const holidayPay = (inputData.specialHours.holiday || 0) * hourlyRate * (1 + this.slovakConstants.holiday_premium_rate);
    
    // Absence pay calculations  
    const sickLeavePay = this.calculateSickLeavePay(inputData.otherAbsences.sickLeave || 0, hourlyRate);
    const vacationPay = inputData.vacationDays * hourlyRate * 8; // 8 hours per day
    
    const specialPay = {
      nightWork: nightWorkPay,
      weekend: weekendPay,
      holiday: holidayPay
    };
    
    const absencePay = {
      sickLeave: sickLeavePay,
      vacation: vacationPay
    };
    
    const total = basePay + overtimePay + nightWorkPay + weekendPay + holidayPay + 
                 sickLeavePay + vacationPay + inputData.bonuses + inputData.otherIncome;
    
    this.log(`Gross components: Base €${basePay}, OT €${overtimePay}, Special €${Object.values(specialPay).reduce((a, b) => a + b, 0)}, Total €${total}`);
    
    return {
      basePay,
      overtimePay,
      specialPay,
      absencePay,
      bonuses: inputData.bonuses,
      otherIncome: inputData.otherIncome,
      total
    };
  }

  /**
   * Calculate insurance contributions for Slovak employees (entity-aware)
   */
  protected async calculateInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>> {
    this.log(`Calculating insurance contributions for entity type: ${this.entityType}`);
    
    const assessmentBase = Math.min(grossComponents.total, this.slovakConstants.max_assessment_base_monthly);
    
    // Get entity-specific rates
    const entityRates = await getEntitySpecificRates(this.businessId, grossComponents.total);
    
    if (!entityRates) {
      this.log('Warning: Could not load entity-specific rates, using default calculation');
      return await this.calculateStandardInsuranceContributions(employee, grossComponents, inputData);
    }

    // Calculate entity-specific insurance contributions
    let socialInsuranceEmployee = 0;
    let socialInsuranceEmployer = 0;
    let healthInsuranceEmployee = 0;
    let healthInsuranceEmployer = 0;

    if (this.entityType === 'zivnost') {
      // For živnosť, handle special rules
      const socialInsuranceRequired = entityRates.requires_social_insurance;
      
      healthInsuranceEmployee = Math.max(
        assessmentBase * entityRates.health_insurance_rate,
        entityRates.minimum_health_payment
      );
      
      if (socialInsuranceRequired) {
        socialInsuranceEmployee = Math.max(
          assessmentBase * entityRates.social_insurance_rate,
          entityRates.minimum_social_payment
        );
      }
      
      // No employer contributions for živnosť
      socialInsuranceEmployer = 0;
      healthInsuranceEmployer = 0;
      
    } else {
      // For corporate entities (s.r.o., a.s.), use standard employee calculations
      const contractType = employee.contract_type as ContractType;
      
      switch (contractType) {
        case 'TPP':
        case 'HALF_TIME': {
          const standardSocial = this.calculateSocialInsurance(assessmentBase, false);
          socialInsuranceEmployee = standardSocial.employee.total;
          socialInsuranceEmployer = standardSocial.employer.total;
          break;
        }
        case 'DoVP':
        case 'DoPČ': {
          const contractorSocial = this.calculateContractorSocialInsurance(assessmentBase);
          socialInsuranceEmployee = contractorSocial.employee.total;
          socialInsuranceEmployer = contractorSocial.employer.total;
          break;
        }
        case 'DoBPŠ': {
          const studentSocial = this.calculateStudentSocialInsurance(assessmentBase);
          socialInsuranceEmployee = studentSocial.employee.total;
          socialInsuranceEmployer = studentSocial.employer.total;
          break;
        }
        default:
          socialInsuranceEmployee = 0;
          socialInsuranceEmployer = 0;
      }
      
      const standardHealth = this.calculateHealthInsurance(assessmentBase, false);
      healthInsuranceEmployee = standardHealth.employee;
      healthInsuranceEmployer = standardHealth.employer;
    }
    
    return {
      social: {
        employee: { total: socialInsuranceEmployee },
        employer: { total: socialInsuranceEmployer },
        employeeTotal: socialInsuranceEmployee,
        employerTotal: socialInsuranceEmployer
      },
      health: {
        employee: { main: healthInsuranceEmployee },
        employer: { main: healthInsuranceEmployer },
        employeeTotal: healthInsuranceEmployee,
        employerTotal: healthInsuranceEmployer
      }
    };
  }

  /**
   * Fallback standard insurance calculation
   */
  private async calculateStandardInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>> {
    const assessmentBase = Math.min(grossComponents.total, this.slovakConstants.max_assessment_base_monthly);
    
    // Route to appropriate calculation based on contract type
    let socialInsurance: any;
    const contractType = employee.contract_type as ContractType;
    
    switch (contractType) {
      case 'TPP':
      case 'HALF_TIME':
        socialInsurance = this.calculateSocialInsurance(assessmentBase, false);
        break;
      case 'DoVP':
      case 'DoPČ':
        socialInsurance = this.calculateContractorSocialInsurance(assessmentBase);
        break;
      case 'DoBPŠ':
        socialInsurance = this.calculateStudentSocialInsurance(assessmentBase);
        break;
      default:
        socialInsurance = this.createZeroSocialInsurance();
    }
    
    const healthInsurance = this.calculateHealthInsurance(assessmentBase, false);
    
    return {
      social: {
        employee: socialInsurance.employee,
        employer: socialInsurance.employer,
        employeeTotal: socialInsurance.employee.total,
        employerTotal: socialInsurance.employer.total
      },
      health: {
        employee: { main: healthInsurance.employee },
        employer: { main: healthInsurance.employer },
        employeeTotal: healthInsurance.employee,
        employerTotal: healthInsurance.employer
      }
    };
  }

  /**
   * Calculate tax for Slovak employees
   */
  protected async calculateTax(
    employee: Employee,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    inputData: PayrollInputData
  ): Promise<TaxCalculation> {
    this.log(`Calculating tax`);
    
    const totalInsuranceEmployee = Object.values(insuranceCalculations)
      .reduce((sum, calc) => sum + calc.employeeTotal, 0);
    
    const taxBase = grossComponents.total - totalInsuranceEmployee;
    
    // Apply tax-free allowance
    const taxFreeAllowance = (employee.applies_tax_free_allowance || false) 
      ? this.slovakConstants.tax_free_allowance_monthly 
      : 0;
    
    // Apply spouse allowance if applicable
    const spouseAllowance = (employee.applies_spouse_allowance || false)
      ? (this.slovakConstants.spouse_allowance_monthly || 0)
      : 0;
    
    // Calculate taxable income
    const taxableIncome = Math.max(0, taxBase - taxFreeAllowance - spouseAllowance);
    
    // Calculate tax based on brackets
    let tax19Percent = 0;
    let tax25Percent = 0;
    
    const monthlyTier1Limit = this.slovakConstants.tax_rate_tier1_limit / 12;
    
    if (taxableIncome <= monthlyTier1Limit) {
      tax19Percent = taxableIncome * this.slovakConstants.tax_rate_tier1;
    } else {
      tax19Percent = monthlyTier1Limit * this.slovakConstants.tax_rate_tier1;
      tax25Percent = (taxableIncome - monthlyTier1Limit) * this.slovakConstants.tax_rate_tier2;
    }
    
    const taxPrepayment = tax19Percent + tax25Percent;
    
    // Apply child tax bonus
    const childBonus = this.calculateChildTaxBonus();
    
    // Calculate final tax and potential bonus payout
    let finalTax = taxPrepayment - childBonus;
    let netTaxBenefit = 0;
    
    if (finalTax < 0) {
      netTaxBenefit = Math.abs(finalTax);
      finalTax = 0;
    }
    
    return {
      taxBase,
      taxableIncome,
      allowances: {
        personal: taxFreeAllowance,
        spouse: spouseAllowance
      },
      taxBrackets: {
        tier1: tax19Percent,
        tier2: tax25Percent
      },
      credits: {
        childBonus
      },
      totalTax: finalTax,
      netTaxBenefit
    };
  }

  /**
   * Calculate other deductions for Slovak employees
   */
  protected async calculateOtherDeductions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<DeductionCalculation[]> {
    this.log(`Calculating other deductions`);
    
    const deductions: DeductionCalculation[] = [];
    
    // Meal vouchers
    if (this.monthlyData.meal_vouchers_count > 0) {
      const mealVoucherDeduction = this.monthlyData.meal_vouchers_count * 
        ((this.slovakConstants.meal_voucher_min_value + this.slovakConstants.meal_voucher_max_value) / 2) *
        (1 - this.slovakConstants.meal_voucher_employer_min_contribution);
      
      deductions.push({
        type: 'OTHER',
        amount: mealVoucherDeduction,
        description: 'Meal Vouchers',
        mandatory: false
      });
    }
    
    // Third pillar pension contribution
    if (employee.third_pillar_contribution && employee.third_pillar_contribution > 0) {
      deductions.push({
        type: 'PENSION_CONTRIBUTION',
        amount: employee.third_pillar_contribution,
        description: 'Third Pillar Pension',
        mandatory: false
      });
    }
    
    // Union dues
    if (employee.union_contribution && employee.union_contribution > 0) {
      deductions.push({
        type: 'OTHER',
        amount: employee.union_contribution,
        description: 'Union Dues',
        mandatory: false
      });
    }
    
    // Garnishments
    const garnishments = this.calculateGarnishments();
    if (garnishments > 0) {
      deductions.push({
        type: 'OTHER',
        amount: garnishments,
        description: 'Garnishments',
        mandatory: true
      });
    }
    
    // Advance payments
    if (inputData.advances > 0) {
      deductions.push({
        type: 'OTHER',
        amount: inputData.advances,
        description: 'Advance Payments',
        mandatory: true
      });
    }
    
    // Other deductions
    if (inputData.otherDeductions && Object.keys(inputData.otherDeductions).length > 0) {
      Object.entries(inputData.otherDeductions).forEach(([key, amount]) => {
        if (amount > 0) {
          deductions.push({
            type: 'OTHER',
            amount,
            description: key,
            mandatory: false
          });
        }
      });
    }
    
    return deductions;
  }

  /**
   * Slovak-specific validation
   */
  protected async validateCountrySpecificData(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<Record<string, string[]>> {
    const errors: Record<string, string[]> = {};
    
    // Validate contract type
    if (!employee.contract_type || !['TPP', 'HALF_TIME', 'DoVP', 'DoPČ', 'DoBPŠ'].includes(employee.contract_type)) {
      errors.contract_type = ['Invalid or missing contract type for Slovak employee'];
    }
    
    // Validate rodne cislo if provided
    if (employee.rodne_cislo && !this.validateRodneCislo(employee.rodne_cislo)) {
      errors.rodne_cislo = ['Invalid Slovak personal number (rodné číslo) format'];
    }
    
    return errors;
  }

  /**
   * Helper methods - Slovak-specific calculations
   */

  private convertToSlovakMonthlyData(inputData: PayrollInputData): MonthlyPayrollData {
    return {
      employee_id: inputData.employeeId,
      period_start: inputData.period.start,
      period_end: inputData.period.end,
      regular_hours: inputData.regularHours,
      overtime_hours: inputData.overtimeHours,
      night_hours: inputData.specialHours.night || 0,
      weekend_hours: inputData.specialHours.weekend || 0,
      holiday_hours: inputData.specialHours.holiday || 0,
      vacation_days: inputData.vacationDays,
      sick_leave_days: inputData.sickLeaveDays,
      paid_leave_hours: inputData.otherAbsences.paidLeave || 0,
      unpaid_leave_hours: inputData.otherAbsences.unpaidLeave || 0,
      bonuses: inputData.bonuses,
      commissions: inputData.commissions,
      other_income: inputData.otherIncome,
      meal_vouchers_count: inputData.otherDeductions.mealVouchers || 0,
      advance_payment: inputData.advances,
      other_deductions: Object.values(inputData.otherDeductions).reduce((sum, val) => sum + val, 0)
    };
  }

  private calculateSocialInsurance(assessmentBase: number, isExempt: boolean) {
    if (isExempt) {
      return this.createZeroSocialInsurance();
    }
    
    return {
      employee: {
        pension: assessmentBase * this.slovakConstants.employee_pension_rate,
        disability: assessmentBase * this.slovakConstants.employee_disability_rate,
        sickness: assessmentBase * this.slovakConstants.employee_sickness_rate,
        unemployment: assessmentBase * this.slovakConstants.employee_unemployment_rate,
        total: assessmentBase * (
          this.slovakConstants.employee_pension_rate +
          this.slovakConstants.employee_disability_rate +
          this.slovakConstants.employee_sickness_rate +
          this.slovakConstants.employee_unemployment_rate
        )
      },
      employer: {
        pension: assessmentBase * this.slovakConstants.employer_pension_rate,
        disability: assessmentBase * this.slovakConstants.employer_disability_rate,
        sickness: assessmentBase * this.slovakConstants.employer_sickness_rate,
        unemployment: assessmentBase * this.slovakConstants.employer_unemployment_rate,
        accident: assessmentBase * this.slovakConstants.employer_accident_rate,
        guarantee: assessmentBase * this.slovakConstants.employer_guarantee_rate,
        solidarity: assessmentBase * this.slovakConstants.employer_solidarity_rate,
        total: assessmentBase * (
          this.slovakConstants.employer_pension_rate +
          this.slovakConstants.employer_disability_rate +
          this.slovakConstants.employer_sickness_rate +
          this.slovakConstants.employer_unemployment_rate +
          this.slovakConstants.employer_accident_rate +
          this.slovakConstants.employer_guarantee_rate +
          this.slovakConstants.employer_solidarity_rate
        )
      }
    };
  }

  private calculateContractorSocialInsurance(assessmentBase: number) {
    // Contractors pay reduced rate: 4% pension + 3% disability = 7% total
    return {
      employee: {
        pension: assessmentBase * 0.04,
        disability: assessmentBase * 0.03,
        sickness: 0,
        unemployment: 0,
        total: assessmentBase * 0.07
      },
      employer: {
        pension: assessmentBase * this.slovakConstants.employer_pension_rate,
        disability: assessmentBase * this.slovakConstants.employer_disability_rate,
        sickness: 0,
        unemployment: 0,
        accident: assessmentBase * this.slovakConstants.employer_accident_rate,
        guarantee: assessmentBase * this.slovakConstants.employer_guarantee_rate,
        solidarity: assessmentBase * this.slovakConstants.employer_solidarity_rate,
        total: assessmentBase * (
          this.slovakConstants.employer_pension_rate +
          this.slovakConstants.employer_disability_rate +
          this.slovakConstants.employer_accident_rate +
          this.slovakConstants.employer_guarantee_rate +
          this.slovakConstants.employer_solidarity_rate
        )
      }
    };
  }

  private calculateStudentSocialInsurance(assessmentBase: number) {
    const hasStudentExemption = (this.employee.is_student || false) && 
      assessmentBase <= this.slovakConstants.student_exemption_limit;
    
    if (hasStudentExemption) {
      return this.createZeroSocialInsurance();
    }
    
    // Apply exemption if income > €200
    const adjustedBase = Math.max(0, assessmentBase - this.slovakConstants.student_exemption_limit);
    return this.calculateSocialInsurance(adjustedBase, false);
  }

  private calculateHealthInsurance(assessmentBase: number, isExempt: boolean) {
    if (isExempt) {
      return { employee: 0, employer: 0 };
    }
    
    // Use reduced rate for severely disabled
    const employeeRate = (this.employee.has_severe_disability || false) 
      ? (this.slovakConstants.employee_health_rate_disabled || 0.02)
      : this.slovakConstants.employee_health_rate;
    
    const employerRate = (this.employee.has_severe_disability || false)
      ? (this.slovakConstants.employer_health_rate_disabled || 0.05)
      : this.slovakConstants.employer_health_rate;
    
    return {
      employee: assessmentBase * employeeRate,
      employer: assessmentBase * employerRate
    };
  }

  private calculateChildTaxBonus(): number {
    if (!(this.employee.applies_child_tax_bonus || false) || !this.employee.children) {
      return 0;
    }
    
    let totalBonus = 0;
    
    for (const child of this.employee.children) {
      const age = this.calculateAge(child.birth_date);
      
      if (age < 15) {
        totalBonus += this.slovakConstants.child_bonus_under_15;
      } else if (age >= 15 && age < 18) {
        totalBonus += this.slovakConstants.child_bonus_15_18;
      }
      
      // Additional bonus for disabled children
      if (child.is_disabled && this.slovakConstants.child_bonus_disabled) {
        totalBonus += this.slovakConstants.child_bonus_disabled;
      }
    }
    
    return totalBonus;
  }

  private calculateWeekendPay(weekendHours: number): number {
    // Simplified calculation - assume 40% Saturday, 60% Sunday
    const saturdayHours = weekendHours * 0.4;
    const sundayHours = weekendHours * 0.6;
    
    return (saturdayHours * this.slovakConstants.saturday_premium_hourly) +
           (sundayHours * this.slovakConstants.sunday_premium_hourly);
  }

  private calculateSickLeavePay(sickDays: number, hourlyRate: number): number {
    // First 10 days paid by employer at 55% of average wage
    const dailyRate = hourlyRate * 8 * 0.55;
    const daysToPay = Math.min(sickDays, this.slovakConstants.sick_leave_employer_days);
    return daysToPay * dailyRate;
  }

  private calculateGarnishments(): number {
    if (!(this.employee.has_garnishments || false) || !this.employee.garnishments) {
      return 0;
    }
    
    // Sort by priority
    const activeGarnishments = this.employee.garnishments
      .filter(g => g.active)
      .sort((a, b) => a.priority - b.priority);
    
    let totalGarnishments = 0;
    
    for (const garnishment of activeGarnishments) {
      if (garnishment.amount_type === 'FIXED') {
        totalGarnishments += garnishment.amount;
      }
      // Percentage garnishments would need iterative calculation - simplified here
    }
    
    return totalGarnishments;
  }

  private calculateAge(birthDate: string): number {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  private validateRodneCislo(rodneCislo: string): boolean {
    // Basic validation for Slovak personal number format
    const pattern = /^\d{6}\/?\d{3,4}$/;
    return pattern.test(rodneCislo);
  }

  private createZeroSocialInsurance() {
    return {
      employee: {
        pension: 0,
        disability: 0,
        sickness: 0,
        unemployment: 0,
        total: 0
      },
      employer: {
        pension: 0,
        disability: 0,
        sickness: 0,
        unemployment: 0,
        accident: 0,
        guarantee: 0,
        solidarity: 0,
        total: 0
      }
    };
  }
}